/* eslint-disable max-len */
import { useCallback, useState } from 'react';

import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import { useGlobalQueries, useScreenSize } from '@/exceed';

import {
  fetchAgendaItems,
  fetchVideo,
  fetchRelatedVideos,
  fetchRelatedCourses,
  favouriteVideo,
  unfavouriteVideo,
  createVideoEnrollment,
  updateVideoEnrollment,
  updateReview,
} from '../api';

import findNextAgendaItem from '../helpers/findNextAgendaItem';
import getIsUserPublic from '../helpers/getIsUserPublic';

export default () => {
  const isPublicUser = getIsUserPublic(useGlobalQueries());
  const queryClient = useQueryClient();

  const { id, type } = useParams();

  const history = useHistory();

  const screenSize = useScreenSize();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const versionId = searchParams.get('version_id');

  const [selectedAgendaItem, setSelectedAgendaItem] = useState(null);

  const { data: agendaItems, isFetching: isAgendaFetching, refetch: refetchAgendaItems } = useQuery(
    ['videoAgenda', id],
    () => fetchAgendaItems({ id }),
    {
      initialData: [],
      refetchOnWindowFocus: false,
      enabled: type === 'live',
      onSuccess: (data) => {
        const nextAgendaItem = findNextAgendaItem(data);
        setSelectedAgendaItem(nextAgendaItem);
      },
    },
  );

  const {
    data: video,
    isError: hasVideoError,
    isFetching: isVideoFetching,
    isLoading: isVideoLoading,
  } = useQuery(
    ['video', id],
    () => fetchVideo({ id, type, history, versionId }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      structuralSharing: (oldData, newData) => ({
        data: {
          ...newData.data,
          video: oldData?.data?.video || newData.data?.video,
        },
      }),
    },
  );

  const shouldFetchRelatedContent = Boolean(video) && !isPublicUser;

  const { data: relatedVideos } = useQuery(
    ['relatedVideos'],
    () => fetchRelatedVideos({ id: video?.data?.topic?.id, versionId }),
    {
      enabled: shouldFetchRelatedContent,
      initialData: {
        data: [],
      },
    },
  );

  const { data: relatedCourses } = useQuery(
    ['relatedCourses'],
    () => fetchRelatedCourses({ id: video?.data?.id, type }),
    {
      enabled: shouldFetchRelatedContent,
      initialData: [],
      refetchOnWindowFocus: false,
    },
  );

  const { data: app } = useQuery(['app'], () => {}, {
    enabled: false,
  });

  const { mutate: favouriteVideoHandler } = useMutation({
    mutationFn: () => favouriteVideo({ id, type }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['video', id] });

      const previousData = queryClient.getQueryData(['video', id]);

      queryClient.setQueryData(['video', id], (oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          is_favorited: true,
        },
      }));

      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['video', id] });
    },
  });

  const { mutate: unfavouriteVideoHandler } = useMutation({
    mutationFn: () => unfavouriteVideo({ id, type }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['video', id] });

      const previousData = queryClient.getQueryData(['video', id]);

      queryClient.setQueryData(['video', id], (oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          is_favorited: false,
        },
      }));

      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['video', id] });
    },
  });

  const updateReviewMutation = useMutation({
    mutationFn: ({ vote }) => updateReview({ id, vote }),
    onMutate: async ({ vote }) => {
      await queryClient.cancelQueries({ queryKey: ['video', id] });

      const previousData = queryClient.getQueryData(['video', id]);
      const newVoteTotal = previousData.data.actual_vote_total - previousData.data.vote_user + vote;

      queryClient.setQueryData(['video', id], (oldData) => ({
        ...oldData,
        data: {
          ...oldData.data,
          actual_vote_total: newVoteTotal,
          vote_total: Math.max(newVoteTotal, 0),
          vote_user: vote,
        },
      }));

      return { previousData };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(['video', id], context.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['video', id] });
    },
  });

  return {
    video,
    relatedVideos,
    relatedCourses,
    agendaItems,
    screenSize,
    selectedAgendaItem,
    shouldMute: type === 'live',
    isLoading: isVideoLoading || isAgendaFetching || hasVideoError,
    handlers: {
      viewAgendaItem: setSelectedAgendaItem,
      favouriteVideo: favouriteVideoHandler,
      unfavouriteVideo: unfavouriteVideoHandler,
      createEnrollment: useCallback(() => createVideoEnrollment({ id, versionId }), [id, versionId]),
      updateEnrollment: useCallback(
        ({ isComplete, progress, time }) =>
          updateVideoEnrollment({
            id,
            versionId,
            isComplete,
            progress,
            time,
          }),
        [id, versionId],
      ),
      updateReview: updateReviewMutation,
      refetchAgendaItems,
    },
    isVideoFetching,
  };
};
